import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { apiFetch } from '../utils/apiFetch';
import { getCatalogStorageSession, isCatalogStorageSessionCurrent } from '../utils/catalogSessionStorage';
import { AUTH_SESSION_CHANGING_EVENT, AUTH_SESSION_INVALID_EVENT } from '../utils/auth';
import { checkoutMapDistanceMeters, getCheckoutMapPrecisionState } from '../utils/checkoutMapPrecision';
import { isCheckoutPlaceSnapshot } from '../utils/checkoutPlaceSelection';
import { isGoogleCheckoutSnapshot } from '../utils/googleCheckoutSelection';

const TILE_URL = 'https://maps.brutosdelivery.cloud/styles/brutos/{z}/{x}/{y}.png?v=20260930-open-streets-co';
export const normalizeLocationAddress = (value = {}) => ({
  street: String(value.street ?? value.streetName ?? '').trim(),
  number: String(value.number ?? value.streetNumber ?? '').trim(),
  neighborhood: String(value.neighborhood ?? '').trim(),
  city: String(value.city ?? '').trim(),
  state: String(value.state ?? '').trim().toUpperCase(),
  cep: (() => {
    const digits = String(value.cep ?? value.postalCode ?? '').replace(/\D/g, '');
    return digits.length === 8 ? digits : '';
  })(),
  country: String(value.country || 'BR').trim().toUpperCase(),
});
export const locationAddressKey = (value) => JSON.stringify(normalizeLocationAddress(value));
export const validMapPosition = (value) => {
  const lat = value?.lat ?? value?.latitude;
  const lng = value?.lng ?? value?.longitude;
  if ([lat, lng].some((part) => part === null || part === undefined || String(part).trim() === '')) return null;
  const point = { lat: Number(lat), lng: Number(lng) };
  return Number.isFinite(point.lat) && Number.isFinite(point.lng) && Math.abs(point.lat) <= 85.0511
    && Math.abs(point.lng) <= 180 && (point.lat !== 0 || point.lng !== 0) ? point : null;
};
export const isUsableBalcaoLocationReceipt = value => {
  const snapshot = value?.deliveryLocation;
  const point = validMapPosition(snapshot?.position);
  const returnedPoint = validMapPosition(value?.position);
  return Boolean(value?.confirmationToken && snapshot?.schema === 'brutos-delivery-location-v1' && snapshot.version === 1
    && ['confirmed', 'estimated'].includes(snapshot.status) && snapshot.producer === 'delivery' && point && returnedPoint
    && point.lat === returnedPoint.lat && point.lng === returnedPoint.lng
    && (snapshot.status === 'estimated' ? isCheckoutPlaceSnapshot(snapshot) || isGoogleCheckoutSnapshot(snapshot) || (snapshot.quality?.level === 'street-estimate'
      && ['photon', 'overture-cnefe'].includes(snapshot.quality?.provider))
      : ['house', 'human-confirmed'].includes(snapshot.quality?.level)));
};
const project = ({ lat, lng }, zoom) => {
  const scale = 256 * 2 ** zoom;
  const sine = Math.sin(lat * Math.PI / 180);
  return { x: (lng + 180) / 360 * scale, y: (0.5 - Math.log((1 + sine) / (1 - sine)) / (4 * Math.PI)) * scale };
};
const unproject = ({ x, y }, zoom) => {
  const scale = 256 * 2 ** zoom;
  return { lng: x / scale * 360 - 180, lat: Math.atan(Math.sinh(Math.PI * (1 - 2 * y / scale))) * 180 / Math.PI };
};
export const mapPositionFromClientPoint = ({ clientX, clientY, rect, center, zoom, width, height }) => {
  const pixel = project(center, zoom);
  return validMapPosition(unproject({
    x: pixel.x + clientX - rect.left - width / 2,
    y: pixel.y + clientY - rect.top - height / 2,
  }, zoom));
};
export const mapPositionFromKeyboard = ({ key, position, center, zoom, step = 20 }) => {
  const base = validMapPosition(position) || validMapPosition(center);
  if (!base) return null;
  if (key === 'Enter') return base;
  const delta = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[key];
  if (!delta) return null;
  const basePixel = project(base, zoom);
  return validMapPosition(unproject({ x: basePixel.x + delta[0], y: basePixel.y + delta[1] }, zoom));
};
export const mapHeightForWidth = (width) => Math.max(1, Math.round((Number(width) || 480) * 0.75));

// The map only selects intent. The backend authorizes the tenant, area and receipt.
export const LocationPinMap = ({ position, initialCenter, onChange, disabled = false }) => {
  const rootRef = useRef(null);
  const dragRef = useRef(null);
  const [width, setWidth] = useState(480);
  const [center, setCenter] = useState(() => validMapPosition(initialCenter) || validMapPosition(position));
  const [zoom, setZoom] = useState(16);
  const [tileError, setTileError] = useState(false);
  const centerLatitude = initialCenter?.lat ?? initialCenter?.latitude;
  const centerLongitude = initialCenter?.lng ?? initialCenter?.longitude;
  useEffect(() => {
    const next = validMapPosition({ lat: centerLatitude, lng: centerLongitude });
    if (next) setCenter(next);
  }, [centerLatitude, centerLongitude]);
  useEffect(() => {
    const resize = () => {
      const nextWidth = rootRef.current?.clientWidth || 480;
      setWidth((current) => current === nextWidth ? current : nextWidth);
    };
    resize();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(resize) : null;
    if (rootRef.current) observer?.observe(rootRef.current);
    window.addEventListener('resize', resize);
    return () => { observer?.disconnect(); window.removeEventListener('resize', resize); };
  }, []);
  if (!center) return <Alert severity="info">Selecione uma sugestão de endereço ou informe as coordenadas da entrada para abrir o mapa.</Alert>;
  const height = mapHeightForWidth(width);
  const pixel = project(center, zoom);
  const tiles = [];
  const minX = Math.floor((pixel.x - width / 2) / 256);
  const minY = Math.floor((pixel.y - height / 2) / 256);
  for (let x = minX; x <= Math.ceil((pixel.x + width / 2) / 256); x += 1) {
    for (let y = minY; y <= Math.ceil((pixel.y + height / 2) / 256); y += 1) {
      if (y < 0 || y >= 2 ** zoom) continue;
      const tileX = ((x % (2 ** zoom)) + 2 ** zoom) % (2 ** zoom);
      tiles.push(<img key={`${zoom}:${x}:${y}`} alt="" draggable={false} onError={() => setTileError(true)}
        src={TILE_URL.replace('{z}', zoom).replace('{x}', tileX).replace('{y}', y)}
        style={{ position: 'absolute', left: x * 256 - pixel.x + width / 2, top: y * 256 - pixel.y + height / 2, width: 256, height: 256, userSelect: 'none' }} />);
    }
  }
  const start = (x, y) => { if (!disabled) dragRef.current = { x, y, pixel }; };
  const select = (next) => {
    if (!next) return;
    onChange(next);
  };
  const end = (x, y) => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag || disabled) return;
    const dx = x - drag.x;
    const dy = y - drag.y;
    if (Math.abs(dx) + Math.abs(dy) > 6) {
      const next = validMapPosition(unproject({ x: drag.pixel.x - dx, y: drag.pixel.y - dy }, zoom));
      if (next) setCenter(next);
    } else {
      const rect = rootRef.current.getBoundingClientRect();
      select(mapPositionFromClientPoint({ clientX: x, clientY: y, rect, center, zoom, width, height }));
    }
  };
  const marker = position ? project(position, zoom) : null;
  return <Stack spacing={1}>
    <Box ref={rootRef} role="application" aria-label="Mapa de confirmação da entrada" tabIndex={disabled ? -1 : 0}
      onMouseDown={(event) => start(event.clientX, event.clientY)} onMouseUp={(event) => end(event.clientX, event.clientY)}
      onMouseLeave={() => { dragRef.current = null; }}
      onTouchStart={(event) => { if (event.touches.length === 1) start(event.touches[0].clientX, event.touches[0].clientY); }}
      onTouchEnd={(event) => { if (event.changedTouches.length === 1) end(event.changedTouches[0].clientX, event.changedTouches[0].clientY); }}
      onTouchCancel={() => { dragRef.current = null; }}
      onKeyDown={(event) => {
        if (disabled) return;
        const next = mapPositionFromKeyboard({ key: event.key, position, center, zoom });
        if (next) { event.preventDefault(); select(next); }
      }} sx={{ position: 'relative', alignSelf: 'center', width: '100%', maxWidth: '96vh', height, boxSizing: 'border-box', overflow: 'hidden', bgcolor: '#e8ecef', borderRadius: 2, border: '1px solid #94a3b8', touchAction: 'none', cursor: disabled ? 'default' : 'crosshair' }}>
      {tiles}
      {marker && <Box aria-hidden="true" sx={{ position: 'absolute', left: marker.x - pixel.x + width / 2, top: marker.y - pixel.y + height / 2, transform: 'translate(-50%, -100%)', fontSize: 32, pointerEvents: 'none', filter: 'drop-shadow(0 1px 2px white)' }}>📍</Box>}
      <Box sx={{ position: 'absolute', right: 4, bottom: 2, bgcolor: 'rgba(255,255,255,.9)', px: 0.5, fontSize: 10 }}><a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">© OpenStreetMap</a></Box>
    </Box>
    <Stack direction="row" spacing={1}>
      <Button disabled={disabled || zoom >= 19} onClick={() => { setZoom((value) => value + 1); setTileError(false); }} aria-label="Aproximar mapa">+</Button>
      <Button disabled={disabled || zoom <= 10} onClick={() => { setZoom((value) => value - 1); setTileError(false); }} aria-label="Afastar mapa">−</Button>
      <Button disabled={disabled} onClick={() => select(center)}>Marcar centro do mapa</Button>
    </Stack>
    {tileError && <Alert severity="warning">Algumas imagens do mapa não carregaram. Confira as coordenadas ou tente novamente antes de confirmar.</Alert>}
    <Typography variant="caption">Confira o pino sugerido e confirme se estiver correto. Para ajustar, arraste o mapa e toque na entrada; pelo teclado, use as setas e Enter.</Typography>
  </Stack>;
};

const LocationConfirmation = forwardRef(function LocationConfirmation({ apiUrl, addressDetails, initialPosition, initialPositionQuality = '', initialSuggestedPosition, initialSuggestedPositionQuality = '', initialMapCenter, initialMapPreviewQuality = '', mapPreviewUnavailable = false, purpose = 'destination', balcaoRecovery = false, placeSelectionToken = '', selectedPlace = null, onContext, onResolvedAddress, onChange, onAddressCorrection, onNeighborhoodSuggestion, onDiscardPostalCode, onMapOpenChange, onPolicyChange, policyEnabled, storeOriginVerified, disabled = false }, ref) {
  const recoverBalcao = balcaoRecovery === true && purpose === 'destination';
  const address = normalizeLocationAddress(addressDetails);
  const key = locationAddressKey(address);
  const selectedPlacePoint = recoverBalcao ? validMapPosition(selectedPlace?.position) : null;
  const selectedPlaceKey = selectedPlacePoint && selectedPlace?.id
    ? JSON.stringify([selectedPlace.id, selectedPlacePoint.lat, selectedPlacePoint.lng]) : '';
  const currentRef = useRef({ key, disabled });
  currentRef.current = { key, disabled, placeSelectionToken: recoverBalcao ? placeSelectionToken : '', selectedPlaceKey };
  const generationRef = useRef(0);
  const busyPathRef = useRef('');
  const gpsRequestRef = useRef(0);
  const ownerSessionRef = useRef(getCatalogStorageSession());
  const mountedRef = useRef(true);
  const receiptRef = useRef(null);
  const receiptSelectionRef = useRef('');
  const receiptPlaceRef = useRef('');
  const canonicalTransitionRef = useRef('');
  const contextCallbackRef = useRef(onContext); contextCallbackRef.current = onContext;
  const resolvedAddressCallbackRef = useRef(onResolvedAddress); resolvedAddressCallbackRef.current = onResolvedAddress;
  const expiryTimerRef = useRef(null);
  const callbackRef = useRef(onChange);
  callbackRef.current = onChange;
  const correctionCallbackRef = useRef(onAddressCorrection);
  correctionCallbackRef.current = onAddressCorrection;
  const neighborhoodCallbackRef = useRef(onNeighborhoodSuggestion);
  neighborhoodCallbackRef.current = onNeighborhoodSuggestion;
  const mapOpenCallbackRef = useRef(onMapOpenChange);
  mapOpenCallbackRef.current = onMapOpenChange;
  const policyCallbackRef = useRef(onPolicyChange);
  policyCallbackRef.current = onPolicyChange;
  const [context, setContext] = useState(null);
  const [position, setPosition] = useState(() => validMapPosition(initialPosition));
  const [suggestedPosition, setSuggestedPosition] = useState(() => validMapPosition(initialSuggestedPosition));
  const [mapCenter, setMapCenter] = useState(() => validMapPosition(initialMapCenter) || validMapPosition(initialPosition));
  const [positionQuality, setPositionQuality] = useState(initialPositionQuality);
  const [mapPreviewQuality, setMapPreviewQuality] = useState(initialMapPreviewQuality || 'origin-reference');
  const [mapOpen, setMapOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [pendingNeighborhoodSuggestion, setPendingNeighborhoodSuggestion] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [chosen, setChosen] = useState(false);
  const [locating, setLocating] = useState(false);
  const [coordinateDraft, setCoordinateDraft] = useState({ lat: '', lng: '' });
  const selectedPointRef = useRef({ position, chosen, key });
  selectedPointRef.current = { position, chosen, key: chosen ? selectedPointRef.current.key : key };
  const previousKeyRef = useRef(key);
  const reset = useCallback(() => {
    window.clearTimeout(expiryTimerRef.current);
    generationRef.current += 1;
    busyPathRef.current = '';
    receiptRef.current = null;
    receiptSelectionRef.current = '';
    receiptPlaceRef.current = '';
    canonicalTransitionRef.current = '';
    selectedPointRef.current = { ...selectedPointRef.current, chosen: false };
    setConfirmed(false);
    setChosen(false);
    setBusy(false);
    setError('');
    setErrorCode('');
    setPendingNeighborhoodSuggestion(null);
    callbackRef.current?.(null);
  }, []);
  const selectionKey = JSON.stringify([placeSelectionToken, selectedPlaceKey]);
  const previousPlaceSelectionRef = useRef(selectionKey);
  useEffect(() => {
    if (!recoverBalcao || previousPlaceSelectionRef.current === selectionKey) return;
    previousPlaceSelectionRef.current = selectionKey;
    reset();
  }, [recoverBalcao, selectionKey, reset]);
  useEffect(() => {
    if (previousKeyRef.current === key) return;
    previousKeyRef.current = key;
    if (canonicalTransitionRef.current === key && receiptRef.current?.key === key) {
      canonicalTransitionRef.current = ''; return;
    }
    selectedPointRef.current = { position: null, chosen: false };
    gpsRequestRef.current += 1;
    reset();
    setPosition(null);
    setSuggestedPosition(null);
    setMapCenter(null);
    setPositionQuality('origin-reference');
    setMapPreviewQuality('origin-reference');
    setMapOpen(false);
    setLocating(false);
  }, [key, reset]);
  useEffect(() => { mapOpenCallbackRef.current?.(mapOpen); }, [mapOpen]);
  useEffect(() => {
    const invalidate = () => { gpsRequestRef.current += 1; setLocating(false); reset(); setContext(null); setPosition(null); setMapOpen(false); };
    window.addEventListener(AUTH_SESSION_CHANGING_EVENT, invalidate);
    window.addEventListener(AUTH_SESSION_INVALID_EVENT, invalidate);
    const storage = () => { if (!isCatalogStorageSessionCurrent(ownerSessionRef.current)) invalidate(); };
    window.addEventListener('storage', storage);
    return () => {
      window.removeEventListener(AUTH_SESSION_CHANGING_EVENT, invalidate);
      window.removeEventListener(AUTH_SESSION_INVALID_EVENT, invalidate);
      window.removeEventListener('storage', storage);
    };
  }, [reset]);
  const initialLatitude = initialPosition?.lat ?? initialPosition?.latitude;
  const initialLongitude = initialPosition?.lng ?? initialPosition?.longitude;
  useEffect(() => {
    const point = validMapPosition({ lat: initialLatitude, lng: initialLongitude });
    if (point && !selectedPointRef.current.chosen && !selectedPointRef.current.position) {
      setPosition(point); setMapCenter(point); setPositionQuality(initialPositionQuality || 'street-estimate');
    }
  }, [initialLatitude, initialLongitude, initialPositionQuality]);
  const previewLatitude = initialMapCenter?.lat ?? initialMapCenter?.latitude;
  const previewLongitude = initialMapCenter?.lng ?? initialMapCenter?.longitude;
  useEffect(() => {
    if (selectedPointRef.current.chosen) return;
    const center = validMapPosition({ lat: previewLatitude, lng: previewLongitude });
    if (center) setMapCenter(center);
    if (initialMapPreviewQuality) setMapPreviewQuality(initialMapPreviewQuality);
  }, [previewLatitude, previewLongitude, initialMapPreviewQuality]);
  const suggestedLatitude = initialSuggestedPosition?.lat ?? initialSuggestedPosition?.latitude;
  const suggestedLongitude = initialSuggestedPosition?.lng ?? initialSuggestedPosition?.longitude;
  useEffect(() => {
    const suggestion = validMapPosition({ lat: suggestedLatitude, lng: suggestedLongitude });
    setSuggestedPosition(suggestion);
    if (suggestion && purpose === 'store' && !selectedPointRef.current.chosen && !selectedPointRef.current.position) {
      setPosition(suggestion);
      setMapCenter(suggestion);
      setPositionQuality(initialSuggestedPositionQuality || initialMapPreviewQuality || 'street-estimate');
    }
  }, [suggestedLatitude, suggestedLongitude, initialSuggestedPositionQuality, initialMapPreviewQuality, purpose]);
  useEffect(() => {
    mountedRef.current = true;
    reset();
    const session = ownerSessionRef.current;
    let active = true;
    apiFetch(`${apiUrl}/api/delivery-pricing/location-context`).then(async (response) => {
      const data = await response.json().catch(() => ({}));
      if (!active || !isCatalogStorageSessionCurrent(session)) return;
      if (!response.ok) throw new Error(data.error || 'Não foi possível carregar a origem da loja.');
      setContext(data);
      if (recoverBalcao) contextCallbackRef.current?.(data);
      policyCallbackRef.current?.(data.locationPolicyEnabled);
      const origin = validMapPosition(data.origin);
      if (origin && (purpose !== 'store' || locationAddressKey(data.address) === currentRef.current.key)) {
        setMapCenter((current) => current || origin);
      }
    }).catch((requestError) => { if (active) setError(requestError.message); });
    return () => { active = false; mountedRef.current = false; generationRef.current += 1; gpsRequestRef.current += 1; receiptRef.current = null; window.clearTimeout(expiryTimerRef.current); };
  }, [apiUrl, purpose, recoverBalcao, reset]);
  const previousPolicyRef = useRef(policyEnabled);
  useEffect(() => {
    const previous = previousPolicyRef.current;
    previousPolicyRef.current = policyEnabled;
    if (policyEnabled !== true || previous === true) return undefined;
    let active = true;
    // Activation can arrive from another tab. Refresh context without discarding an
    // already signed receipt or interrupting a confirmation for the current draft.
    apiFetch(`${apiUrl}/api/delivery-pricing/location-context`).then(async (response) => {
      const data = await response.json().catch(() => ({}));
      if (!active || !isCatalogStorageSessionCurrent(ownerSessionRef.current)) return;
      if (!response.ok) throw new Error(data.error || 'Não foi possível atualizar a origem da loja.');
      setContext(data);
    }).catch((requestError) => { if (active && isCatalogStorageSessionCurrent(ownerSessionRef.current)) setError(requestError.message); });
    return () => { active = false; };
  }, [apiUrl, policyEnabled]);
  const getValue = () => {
    const receipt = receiptRef.current;
    return receipt && receipt.key === currentRef.current.key && receiptSelectionRef.current === currentRef.current.placeSelectionToken
      && receiptPlaceRef.current === currentRef.current.selectedPlaceKey && isCatalogStorageSessionCurrent(receipt.session)
      && (!receipt.expiresAt || Date.parse(receipt.expiresAt) > Date.now()) ? receipt : null;
  };
  const request = async (path, body) => {
    const session = ownerSessionRef.current;
    if (!isCatalogStorageSessionCurrent(session)) throw new Error('Sua sessão mudou. Reabra esta tela.');
    const generation = ++generationRef.current;
    const requestKey = key;
    const requestPlaceSelection = currentRef.current.placeSelectionToken;
    const requestPlaceKey = currentRef.current.selectedPlaceKey;
    const selectedRequestPoint = recoverBalcao && selectedPointRef.current.chosen && selectedPointRef.current.key === requestKey
      ? validMapPosition(selectedPointRef.current.position) : null;
    let acceptedKey = requestKey;
    busyPathRef.current = path;
    setBusy(true);
    setError('');
    setErrorCode('');
    const current = () => mountedRef.current && generation === generationRef.current && acceptedKey === currentRef.current.key && requestPlaceSelection === currentRef.current.placeSelectionToken
      && requestPlaceKey === currentRef.current.selectedPlaceKey && isCatalogStorageSessionCurrent(session);
    try {
      const response = await apiFetch(`${apiUrl}/api/delivery-pricing/${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const data = await response.json().catch(() => ({}));
      if (!current()) throw new Error('O endereço ou a sessão mudou. Confirme novamente a localização.');
      if (!response.ok || !data.confirmationToken) {
        if (recoverBalcao && response.status >= 500) throw Object.assign(new Error(data.error || 'A verificação do endereço está temporariamente indisponível. Tente novamente.'), { code: data.code || '' });
        if (recoverBalcao && requestPlaceSelection && path === 'resolve-balcao-destination') {
          setMapOpen(false);
          throw Object.assign(new Error(data.error || 'Selecione o local novamente para verificar sua localização.'), { code: data.code || '' });
        }
        if (data.code === 'DELIVERY_LOCATION_ADDRESS_NEIGHBORHOOD_MISMATCH' && data.suggestedAddress?.neighborhood) {
          const proposal = {
            neighborhood: String(data.suggestedAddress.neighborhood).trim(),
            cep: String(data.suggestedAddress.postalCode || data.suggestedAddress.cep || '').replace(/\D/g, '').slice(0, 8),
            key: requestKey,
          };
          if (neighborhoodCallbackRef.current) neighborhoodCallbackRef.current(proposal);
          else if (correctionCallbackRef.current) setPendingNeighborhoodSuggestion(proposal);
          setMapOpen(false);
          throw new Error(data.error || 'Confira o bairro indicado para este número antes de continuar.');
        }
        if (data.code === 'DELIVERY_LOCATION_ADDRESS_CORRECTION_REQUIRED' && data.correctedAddress) {
          correctionCallbackRef.current?.(data.correctedAddress);
          setMapOpen(false);
          throw new Error(data.error || 'O CEP foi corrigido pela base postal. Confira o endereço e verifique novamente.');
        }
        if (data.code === 'DELIVERY_LOCATION_ADDRESS_POSTAL_MISMATCH') {
          setMapOpen(false);
          throw Object.assign(new Error(data.error || 'O CEP salvo não corresponde a este endereço.'), { code: data.code });
        }
        const candidate = validMapPosition(data.position || data.details?.position);
        const candidateQuality = data.positionQuality || data.details?.positionQuality || 'origin-reference';
        if (selectedRequestPoint) {
          // A failed lookup must never move the entrance already selected by the operator.
          setPosition(selectedRequestPoint);
        } else if (candidate) {
          setPosition(candidate);
          setMapCenter(candidate);
          // A response without provenance does not prove this is a destination pin.
          setPositionQuality(candidateQuality);
        } else {
          setPosition(null);
          setMapCenter(purpose === 'store' ? null : validMapPosition(context?.origin));
          setPositionQuality('origin-reference');
        }
        setMapOpen(true);
        const needsMapConfirmation = data.code === 'DELIVERY_LOCATION_CONFIRMATION_REQUIRED' && candidate
          && ['street-estimate', 'nearby-house-estimate', 'house-unverified'].includes(candidateQuality);
        const requestError = new Error(needsMapConfirmation
          ? 'Confira a entrada no mapa para continuar.'
          : data.error || data.message || 'Confira a entrada no mapa e confirme o ponto.');
        requestError.code = data.code || '';
        requestError.positionQuality = data.code === 'DELIVERY_LOCATION_CONFIRMATION_REQUIRED' && candidate
          ? candidateQuality : '';
        throw requestError;
      }
      if (recoverBalcao && !isUsableBalcaoLocationReceipt(data)) throw new Error('Não há uma localização válida para esta entrega. Confira o endereço ou marque a entrada no mapa.');
      if (selectedRequestPoint && path === 'confirm-balcao-destination') {
        const returned = validMapPosition(data.deliveryLocation.position);
        // The shared server contract canonicalizes coordinates to seven decimals.
        if (returned.lat !== Number(selectedRequestPoint.lat.toFixed(7)) || returned.lng !== Number(selectedRequestPoint.lng.toFixed(7)))
          throw new Error('A localização recebida não corresponde ao ponto marcado. Confirme novamente a entrada no mapa.');
      }
      if (recoverBalcao && requestPlaceSelection && path === 'resolve-balcao-destination'
        && !isCheckoutPlaceSnapshot(data.deliveryLocation)) throw new Error('Selecione novamente o local para verificar sua localização.');
      if (recoverBalcao && requestPlaceSelection && path === 'resolve-balcao-destination') {
        const returned = validMapPosition(data.deliveryLocation.position);
        if (!requestPlaceKey || JSON.stringify([data.deliveryLocation.quality.candidateId, returned.lat, returned.lng]) !== requestPlaceKey)
          throw new Error('A localização recebida não corresponde ao local selecionado. Selecione o local novamente.');
      }
      const canonicalAddress = recoverBalcao ? normalizeLocationAddress(data.addressDetails || data.deliveryLocation?.address) : address;
      const receiptKey = recoverBalcao ? locationAddressKey(canonicalAddress) : requestKey;
      const receipt = { ...data, addressDetails: canonicalAddress, key: receiptKey, session };
      if (data.expiresAt && (!Number.isFinite(Date.parse(data.expiresAt)) || Date.parse(data.expiresAt) <= Date.now())) {
        throw new Error('A confirmação expirou. Confira novamente a localização.');
      }
      receiptRef.current = receipt;
      receiptSelectionRef.current = requestPlaceSelection;
      receiptPlaceRef.current = requestPlaceKey;
      if (recoverBalcao && receiptKey !== requestKey) {
        canonicalTransitionRef.current = receiptKey;
        acceptedKey = receiptKey;
        resolvedAddressCallbackRef.current?.(canonicalAddress, requestKey, { placePoint: isCheckoutPlaceSnapshot(data.deliveryLocation) });
      }
      window.clearTimeout(expiryTimerRef.current);
      if (data.expiresAt) expiryTimerRef.current = window.setTimeout(() => {
        if (receiptRef.current === receipt && mountedRef.current) { reset(); setError('A confirmação expirou. Confira novamente a localização.'); }
      }, Date.parse(data.expiresAt) - Date.now());
      setPosition(validMapPosition(data.position || data.deliveryLocation?.position || data.deliveryLocation?.coordinates) || position);
      setPositionQuality(data.deliveryLocation?.status === 'estimated' ? data.deliveryLocation.quality.level : 'confirmed');
      setConfirmed(true);
      callbackRef.current?.(receipt);
      setBusy(false);
      return receipt;
    } catch (requestError) {
      if (current()) { setError(requestError.message); setErrorCode(requestError.code || ''); receiptRef.current = null; setConfirmed(false); callbackRef.current?.(null); }
      throw requestError;
    } finally { if (current()) setBusy(false); }
  };
  const openMap = (candidate) => {
    const point = validMapPosition(candidate);
    if (point) { setPosition(point); setSuggestedPosition(point); setPositionQuality('saved-origin-suggestion'); setMapCenter(point); }
    else if (!mapCenter && purpose !== 'store') setMapCenter(validMapPosition(context?.origin));
    setMapOpen(true);
  };
  const ensureConfirmed = async () => {
    if (!recoverBalcao && purpose === 'destination' && policyEnabled !== true && context?.locationPolicyEnabled !== true && (policyEnabled === false || context?.locationPolicyEnabled === false)) {
      if (!isCatalogStorageSessionCurrent(ownerSessionRef.current)) throw new Error('Sua sessão mudou. Reabra esta tela.');
      return { legacy: true, session: ownerSessionRef.current };
    }
    const existing = getValue();
    if (existing) return existing;
    if (currentRef.current.disabled) throw new Error('Aguarde a operação atual antes de confirmar a localização.');
    if (purpose === 'store') { setMapOpen(true); throw new Error('Marque e confirme a entrada da loja no mapa.'); }
    const selected = selectedPointRef.current;
    const selectedPosition = validMapPosition(selected.position);
    if (recoverBalcao && selected.chosen && selected.key === currentRef.current.key && selectedPosition) {
      return request('confirm-balcao-destination', { purpose, addressDetails: address, position: selectedPosition, humanConfirmed: true });
    }
    return request(recoverBalcao ? 'resolve-balcao-destination' : 'resolve-destination', {
      addressDetails: address,
      ...(recoverBalcao && placeSelectionToken ? { placeSelectionToken } : {}),
    });
  };
  const acceptGoogleReceipt = (data, expectedKey) => {
    const session = ownerSessionRef.current;
    if (!recoverBalcao || !mountedRef.current || currentRef.current.disabled || currentRef.current.key !== expectedKey
      || !isCatalogStorageSessionCurrent(session) || !isUsableBalcaoLocationReceipt(data)
      || !isGoogleCheckoutSnapshot(data.deliveryLocation) || !Number.isFinite(Date.parse(data.expiresAt))
      || Date.parse(data.expiresAt) <= Date.now() || locationAddressKey(data.addressDetails || data.deliveryLocation.address) !== expectedKey) {
      throw new Error('O endereço ou a sessão mudou. Selecione novamente a localização.');
    }
    reset();
    const receipt = { ...data, key: expectedKey, session };
    receiptRef.current = receipt; receiptSelectionRef.current = currentRef.current.placeSelectionToken;
    receiptPlaceRef.current = currentRef.current.selectedPlaceKey;
    setPosition(data.deliveryLocation.position); setMapCenter(data.deliveryLocation.position);
    setPositionQuality('user-selected-point'); setMapOpen(false); setConfirmed(true);
    expiryTimerRef.current = window.setTimeout(() => {
      if (mountedRef.current && receiptRef.current === receipt) {
        reset(); setPosition(null); setMapCenter(null);
        setError('A localização selecionada expirou. Faça uma nova busca para continuar.');
      }
    }, Math.min(Date.parse(data.expiresAt), Date.parse(data.deliveryLocation.quality.google.deleteAt)) - Date.now());
    callbackRef.current?.(receipt);
    return receipt;
  };
  const cancelPendingVerification = () => {
    generationRef.current += 1; gpsRequestRef.current += 1; busyPathRef.current = '';
    setBusy(false); setLocating(false);
  };
  useImperativeHandle(ref, () => ({ ensureConfirmed, getValue, reset, openMap, acceptGoogleReceipt, cancelPendingVerification, isMapOpen: () => mapOpen }));
  const choose = (next) => {
    const point = validMapPosition(next);
    if (!point) return;
    gpsRequestRef.current += 1;
    reset();
    selectedPointRef.current = { position: point, chosen: true, key: currentRef.current.key };
    setPosition(point);
    setChosen(true);
    setLocating(false);
  };
  const useCurrentLocation = () => {
    if (recoverBalcao && !window.confirm('Você está no endereço da entrega? Se estiver na loja ou em outro lugar, marque a entrada do cliente no mapa.')) {
      setMapOpen(true); return;
    }
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setError('A localização não está disponível neste navegador. Marque a entrada no mapa.');
      return;
    }
    const requestKey = currentRef.current.key;
    const gpsRequest = ++gpsRequestRef.current;
    const session = ownerSessionRef.current;
    setLocating(true);
    setError('');
    navigator.geolocation.getCurrentPosition((result) => {
      if (gpsRequest !== gpsRequestRef.current || requestKey !== currentRef.current.key || !isCatalogStorageSessionCurrent(session) || !mountedRef.current) return;
      setLocating(false);
      const point = validMapPosition({ lat: result.coords.latitude, lng: result.coords.longitude });
      if (!point) { setError('Não foi possível obter um ponto válido. Marque a entrada no mapa.'); return; }
      reset();
      selectedPointRef.current = { position: point, chosen: true, key: requestKey };
      setPosition(point);
      setMapCenter(point);
      setPositionQuality(Number(result.coords.accuracy) <= 50 ? 'gps' : 'street-estimate');
      setChosen(true);
      setMapOpen(true);
    }, (failure) => {
      if (gpsRequest !== gpsRequestRef.current || requestKey !== currentRef.current.key || !isCatalogStorageSessionCurrent(session) || !mountedRef.current) return;
      setLocating(false);
      setError(failure?.code === 1
        ? 'Permita o acesso à localização no navegador ou marque a entrada no mapa.'
        : 'Não foi possível obter sua localização. Marque a entrada no mapa.');
    }, { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 });
  };
  const originPosition = validMapPosition(context?.origin);
  const movementMeters = positionQuality === 'origin-reference' && originPosition && position && purpose !== 'store'
    ? checkoutMapDistanceMeters(originPosition, position)
    : 0;
  const mapFallbackCenter = purpose === 'store' ? null : originPosition;
  const precision = getCheckoutMapPrecisionState(positionQuality || 'origin-reference', chosen, movementMeters,
    purpose === 'destination' && Boolean(position));
  const isSuggestedStorePoint = purpose === 'store' && !chosen && Boolean(position && suggestedPosition
    && position.lat === suggestedPosition.lat && position.lng === suggestedPosition.lng);
  const hasManualBalcaoPoint = recoverBalcao && chosen && Boolean(validMapPosition(position));
  // A sugestão só vira confirmação após o operador pressionar este CTA. Um pino
  // vindo da busca, isoladamente, nunca emite o recibo HMAC.
  const canConfirmPoint = purpose === 'store' ? Boolean(position && (chosen || isSuggestedStorePoint)) : (hasManualBalcaoPoint || precision.canConfirm) && Boolean(position);
  const googleSelected = isGoogleCheckoutSnapshot(receiptRef.current?.deliveryLocation);
  const mapInteractionDisabled = disabled || (busy && (!recoverBalcao || busyPathRef.current !== 'resolve-balcao-destination'));
  if (!recoverBalcao && purpose === 'destination' && policyEnabled !== true && context?.locationPolicyEnabled !== true && (policyEnabled === false || context?.locationPolicyEnabled === false)) return null;
  return <Stack spacing={1.5} sx={{ border: '1px solid', borderColor: confirmed ? 'success.main' : 'divider', borderRadius: 2, p: 2 }}>
    <Typography fontWeight={800}>{purpose === 'store' ? 'Entrada da loja no mapa' : 'Localização da entrega'}</Typography>
    {confirmed ? <Alert severity="success">{googleSelected ? 'Localização selecionada. Você pode continuar o pedido.' : receiptRef.current?.deliveryLocation?.status === 'estimated' ? 'Endereço localizado. Você pode continuar ou conferir a entrada no mapa.' : 'Localização confirmada para este endereço.'}</Alert>
      : <Typography variant="body2">{hasManualBalcaoPoint ? 'A entrega usará o ponto que você marcou no mapa. Continue para confirmar essa localização.'
        : recoverBalcao ? 'O endereço será verificado antes de continuar. Você também pode conferir a entrada no mapa.' : 'Confira a entrada no mapa antes de confirmar.'}</Typography>}
    {purpose === 'destination' && context?.originVerified === false && <Alert severity="warning">A localização da loja precisa ser confirmada em Entrega e Preparo antes de confirmar destinos.</Alert>}
    {purpose === 'store' && (storeOriginVerified ?? context?.originVerified) === false && <Alert severity="warning">Entrada da loja ainda não confirmada. Confira o ponto físico e salve o endereço para ativar a validação de destinos e a busca em até 50 km.</Alert>}
    {purpose === 'store' && mapPreviewUnavailable && <Alert severity="warning">Não localizamos uma referência para o endereço agora. Confira os campos ou informe as coordenadas para abrir o mapa.</Alert>}
    {error && <Alert severity="warning">{error}</Alert>}
    {purpose === 'destination' && pendingNeighborhoodSuggestion?.key === key && (
      <Button variant="outlined" disabled={disabled || busy} onClick={() => {
        const proposal = pendingNeighborhoodSuggestion;
        if (proposal.key !== currentRef.current.key) return;
        correctionCallbackRef.current?.({ neighborhood: proposal.neighborhood, postalCode: proposal.cep });
        setPendingNeighborhoodSuggestion(null);
      }}>
        Usar {pendingNeighborhoodSuggestion.neighborhood} para este número
      </Button>
    )}
    {purpose === 'destination' && errorCode === 'DELIVERY_LOCATION_ADDRESS_POSTAL_MISMATCH'
      && address.cep && onDiscardPostalCode && (
      <Button variant="outlined" disabled={disabled || busy} onClick={() => onDiscardPostalCode()}>
        Descartar CEP antigo e verificar novamente
      </Button>
    )}
    {!googleSelected && <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      {purpose !== 'store' && <Button disabled={disabled || busy} onClick={() => ensureConfirmed().catch(() => {})}>{busy ? 'Verificando...' : 'Verificar localização'}</Button>}
      <Button disabled={mapInteractionDisabled} onClick={() => {
        if (mapOpen) { gpsRequestRef.current += 1; setLocating(false); }
        setMapOpen((value) => !value);
      }}>{mapOpen ? 'Recolher mapa' : 'Ajustar ponto no mapa'}</Button>
      {purpose === 'destination' && <Button disabled={disabled || busy || locating} onClick={useCurrentLocation}>{locating ? 'Localizando...' : 'Usar minha localização'}</Button>}
    </Stack>}
    {mapOpen && !googleSelected && <>
      {!chosen && (purpose === 'store' ? mapPreviewQuality === 'origin-reference' : positionQuality === 'origin-reference') && <Alert severity="info">O mapa abriu na região da loja porque não há um ponto confiável para este endereço. Marque a entrada correta.</Alert>}
      {!chosen && purpose === 'store' && mapPreviewQuality === 'house' && <Alert severity="info">O pino indica o endereço encontrado. Confira a entrada física; se estiver correto, confirme o ponto sugerido.</Alert>}
      {!chosen && purpose === 'store' && ['street-estimate', 'nearby-house-estimate'].includes(mapPreviewQuality) && <Alert severity="info">O pino indica uma referência próxima nesta rua. Confira a entrada física; confirme a sugestão se estiver correta ou ajuste no mapa.</Alert>}
      {!chosen && purpose === 'store' && mapPreviewQuality === 'city-reference' && <Alert severity="info">O mapa abriu na cidade informada. Marque a entrada física da loja.</Alert>}
      {!chosen && (purpose === 'store' ? mapPreviewQuality : positionQuality) === 'official-address-point' && <Alert severity="info">Número localizado na base oficial. Confira o ponto e confirme se estiver correto.</Alert>}
      {chosen && !hasManualBalcaoPoint && precision.message && <Alert severity="warning">{precision.message}</Alert>}
      <LocationPinMap position={position} initialCenter={mapCenter || mapFallbackCenter} onChange={choose} disabled={mapInteractionDisabled} />
      <details><summary>Informar coordenadas da entrada</summary><Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
        <TextField label="Latitude da entrada" size="small" value={coordinateDraft.lat} disabled={mapInteractionDisabled} onChange={(event) => setCoordinateDraft((value) => ({ ...value, lat: event.target.value }))} />
        <TextField label="Longitude da entrada" size="small" value={coordinateDraft.lng} disabled={mapInteractionDisabled} onChange={(event) => setCoordinateDraft((value) => ({ ...value, lng: event.target.value }))} />
        <Button disabled={mapInteractionDisabled || !validMapPosition(coordinateDraft)} onClick={() => choose(validMapPosition(coordinateDraft))}>Localizar ponto</Button>
      </Stack></details>
      <Button variant="contained" disabled={disabled || busy || !canConfirmPoint || (purpose === 'destination' && context?.originVerified === false)} onClick={() => request(recoverBalcao ? 'confirm-balcao-destination' : 'location-confirmation', {
        purpose, addressDetails: address, position,
        ...(hasManualBalcaoPoint && selectedPointRef.current.key === currentRef.current.key ? { humanConfirmed: true } : {}),
      }).catch(() => {})}>
        {busy ? 'Confirmando...' : purpose === 'store' ? isSuggestedStorePoint ? 'Confirmar ponto sugerido' : 'Confirmar entrada da loja' : 'Confirmar ponto da entrega'}
      </Button>
    </>}
  </Stack>;
});
export default LocationConfirmation;
